import { bottom } from "@popperjs/core"

class Zoomable {
  constructor(wrapper, index, debug=false) {
    if (!wrapper) {
      console.warn('Zoomable: No zoomable image found.')
      return
    }

    this.debug = debug

    this.isMobile = window.innerWidth < 768
    this.count = index + 1

    this.wrapper = wrapper
    this.container = wrapper.closest('.gallery-modal')
    this.parent = wrapper.closest('.gallery-modal__content')
    this.firstChild = document.body.firstChild
    this.backdrop = document.querySelector('.mx-zoomable-background')
    this.loader = wrapper.querySelector('.mx-zoomable-loader__wrapper')
    this.image = wrapper.querySelector('.mx-zoomable-image')
    this.viewbox = wrapper.querySelector('.mx-zoomable-viewbox__wrapper')
    this.indicator = wrapper.querySelector('.mx-zoomable-indicator') || null

    if ( this.isMobile ) {
      this.viewbox.classList.add('mx-zoomable-viewbox--mobile')
      this.indicator?.classList.add('mx-zoomable-indicator--mobile')
    }

    this.viewboxClone = null
    this.cloneImage = null
    this.indicatorClone = null

    const {
      sectionId: id,
      zoomWidth: width,
      zoomHeight: height,
      zoomLevel: zoom,
      zoomOffset: offset,
      zoomPosition: position,
      zoomSource: src,
    } = this.image.dataset

    this.options = {
      id,
      zWidth: parseInt(width) || 200,
      zHeight: parseInt(height) || 200,
      zoom: parseInt(zoom) / 100 || 1.5,
      zOffset: parseInt(offset) || 25,
      position: position || "follow",
      src: src || this.image.src
    }

    this.isModal = this.wrapper.dataset.context
    this.showIndicator = this.indicator !== null
    this.isStatic = this.options.position !== "follow"
    this.isOpen = false
    
    this.init()

    if ( this.isMobile ) {
      this.wrapper.addEventListener('click', () => {
        if ( this.isOpen ) {
          this.remove()
        } else {
          this.attach()
        }
      })
    } else {
      this.wrapper.addEventListener('mouseover', this.attach.bind(this))
      this.wrapper.addEventListener('mousemove', this.move.bind(this))
      this.wrapper.addEventListener('mouseleave', this.remove.bind(this))

      window.addEventListener('resize', this.remove.bind(this))
      
      if ( !this.isModal && !this.isStatic ) {
        window.addEventListener('scroll', () => {
          this.viewboxClone.style.opacity = 0
        })
      }
    }
  }

  attach(e) {
    if ( this.isMobile ) {
      document.body.classList.add('zoom-open')
      document.body.style.overflow = 'hidden'
      this.backdrop.style.display = 'block'
      document.body.appendChild(this.viewboxClone)
      if ( this.parent ) this.parent.style.overflow = 'hidden'
      if ( this.container ) this.container.style.overflow = 'hidden'
      this.viewboxClone.classList.add('is-open')
      this.viewboxClone.addEventListener('click', this.remove.bind(this))
      this.backdrop.addEventListener('click', this.remove.bind(this))
      this.isOpen = true
    } else {
      if ( this.isModal && this.isStatic ) {
        this.wrapper.appendChild(this.viewboxClone)
        this.setModalPosition()

        if ( this.showIndicator ) {
          this.wrapper.appendChild(this.indicatorClone)
        }
      }

      if ( !this.isModal ) {
        document.body.appendChild(this.viewboxClone)
        if ( this.showIndicator ) {
          this.wrapper.insertAdjacentElement('beforeend', this.indicatorClone)
        }
      }
    }
  }

  move(e) {
    this.viewboxClone.style.opacity = 1
    const { clientX, clientY } = e
    const { left, top, width, height } = this.wrapper.getBoundingClientRect()
    const { zWidth, zHeight, zoom } = this.options
    const { scrollY } = window
    
    const x = clientX - left
    const y = clientY - top
    const percentX = (x / width) * 100
    const percentY = (y / height) * 100

    this.cloneImage.style.backgroundPositionX = `${percentX}%`
    this.cloneImage.style.backgroundPositionY = `${percentY}%`

    if ( !this.isStatic ) {
      this.viewboxClone.style.left = `${clientX - zWidth / 2}px`
      this.viewboxClone.style.top = `${scrollY + (clientY - zHeight / 2)}px`
    }

    if ( this.showIndicator ) {
      const indicatorWidth = zWidth / zoom
      const indicatorHeight = zHeight / zoom
      let indicatorX = x - indicatorWidth / 2
      let indicatorY = y - indicatorHeight / 2

      indicatorX = Math.max(0, Math.min(indicatorX, width - indicatorWidth))
      indicatorY = Math.max(0, Math.min(indicatorY, height - indicatorHeight))
    
      this.indicatorClone.style.transform = `translate(${indicatorX}px, ${indicatorY}px)`
    }
  }

  remove() {
    if ( !this.debug ) {
      this.viewboxClone.remove()
    
      if ( this.isMobile ) {
        document.body.classList.remove('zoom-open')
        document.body.style.overflow = ''
        this.backdrop.style.display = ''
        if ( this.parent ) this.parent.style.overflow = ''
        if (this.container ) this.container.style.overflow = ''
        this.viewboxClone.classList.remove('is-open')
        this.isOpen = false
      } else {
        if ( this.showIndicator ) {
          this.indicatorClone.remove()
        }
      }
    }
  }

  setModalPosition() {
    const { width, height } = this.wrapper.getBoundingClientRect()
    const { zHeight, zOffset, position } = this.options

    const rightAlign = `${width + zOffset}px`
    const leftAlign = `${-zOffset}px`
    const topAlign = '0px'
    const centerAlign = `${(height - zHeight) / 2}px`
    const bottomAlign = `${height - zHeight}px`

    const [x, y] = position.split(' ')
    const alignX = x === 'right' ? rightAlign : leftAlign
    const alignY = y === 'top' ? topAlign : y === 'center' ? centerAlign : bottomAlign

    this.viewboxClone.style.left = alignX
    this.viewboxClone.style.top = alignY
  }

  setStaticPosition() {
    const { scrollY } = window
    const { zWidth, zHeight, zOffset, position } = this.options
    const { left, top, width, height } = this.wrapper.getBoundingClientRect()

    const imageTop = top + scrollY
    const imageCenter = scrollY + (top + height / 2)

    const rightAlign = `${left + width + zOffset}px`
    const leftAlign = `${left - (zWidth + zOffset)}px`
    const topAlign = `${imageTop}px`
    const centerAlign = `${imageCenter - zHeight / 2}px`
    const bottomAlign = `${imageTop + height - zHeight}px`

    const [x, y] = position.split(' ')
    const alignX = x === 'right' ? rightAlign : leftAlign
    const alignY = y === 'top' ? topAlign : y === 'center' ? centerAlign : bottomAlign

    this.viewboxClone.style.left = alignX
    this.viewboxClone.style.top = alignY
  }

  configureViewbox() {
    const imgHeight = this.image.height
    const scaleHeight = imgHeight * this.options.zoom
    this.viewbox.classList.add(`viewbox-${this.options.id}`)
    const elementClone = this.clone(this.viewbox)
    this.viewboxClone = elementClone
    this.cloneImage = this.viewboxClone.querySelector('.mx-zoomable-viewbox')

    const imageLoader = new Image();
    imageLoader.src = this.image.dataset.zoomSource;
    imageLoader.onload = () => {
      this.cloneImage.style.backgroundImage = `url(${this.image.dataset.zoomSource})`
      const loaderClone = this.viewboxClone.querySelector('.mx-zoomable-loader__wrapper')
      loaderClone.remove()
  
    }

    if ( this.isMobile ) {
      const newWidth = window.innerWidth - 40
      const scaleFactor = newWidth / this.image.width
      const newHeight = this.image.height * scaleFactor

      if ( newHeight > window.innerHeight ) {
        this.viewboxClone.style.height = `${window.innerHeight - 40}px`
        this.viewboxClone.style.width = `${window.innerWidth - 40}px`
      } else {
        this.viewboxClone.style.height = `${newHeight}px`
        this.viewboxClone.style.width = `${newWidth}px`
        this.viewboxClone.style.top = '50%'
        this.viewboxClone.style.transform = 'translateY(-50%)'
      }
    } else {
      this.viewboxClone.style.width = `${this.options.zWidth}px`;
      this.viewboxClone.style.height = `${this.options.zHeight}px`;
      this.cloneImage.style.backgroundSize = `auto ${scaleHeight}px`;
    }
  }

  configureIndicator() {
    const cloneElement = this.clone(this.indicator)
    const { zWidth, zHeight, zoom } = this.options
    this.indicatorClone = cloneElement

    this.indicatorClone.style.width = `${zWidth / zoom}px`;
    this.indicatorClone.style.height = `${zHeight / zoom}px`;
  }

  clone(element) {
    const clone = element.cloneNode(true)
    clone.classList.remove('hidden')
    return clone
  }

  init() {
    this.configureViewbox()
    if ( this.showIndicator ) {
      this.configureIndicator()
    }
  }

  destroy() {
    this.wrapper.removeEventListener("mouseover", this.attach.bind(this));
    this.wrapper.removeEventListener("mousemove", this.move.bind(this));
    this.wrapper.removeEventListener("mouseleave", this.remove.bind(this));
    this.viewboxClone?.remove();
    this.indicatorClone?.remove();
  }
}

export default Zoomable;